import { useCallback, useEffect, useState } from 'react';

import { useAlapadatok } from '../alapadatok-context.ts';
import { api, type EtteremSor, type SzallitolevelSor, type SzlevLista } from '../api.ts';
import { useCsakSajatSzlev, useFelhasznalo, useMuvelet } from '../felhasznalo-context.ts';
import { AlairasGomb } from '../reszek/AlairasGomb.tsx';
import { SajatAlairas } from '../reszek/SajatAlairas.tsx';
import { ElerhetosegDoboz } from '../reszek/ElerhetosegDoboz.tsx';
import { LevelKuldoPanel, type LevelKeres } from '../reszek/LevelKuldes.tsx';
import { szlevNyomtatas, szlevPdf, kiadasOkmanyokAblak } from '../reszek/szallitolevel-dokumentum.ts';
import {
  NYOMTATO_ALAP,
  NYOMTATO_NYELVEK,
  NYOMTATO_NYELV_NEVEK,
  cimkeElonezet,
  cimkeMeret,
  cimkeTartalom,
  cimketNyomtat,
  nyEmlek,
  nyLevalaszt,
  nyParosit,
  nyTamogatott,
  nyVisszaKapcsol,
  parositottCsatorna,
  parositottNev,
  probaNyomtatas,
  type NyomtatoBeallitas,
  type ParositasMod,
} from '../reszek/cimke-nyomtato.ts';
 import { Auton, Kiadottak, Raktar } from '../reszek/SzallitoRaktar.tsx';
import { SzlevKimutatas, Szurt } from '../reszek/SzallitoKimutatas.tsx';
import type { SzlevKezdo } from './SoforJaratlap.tsx';
import { ft, szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';

/**
 * Szállítólevél. (3.0 / 5.)
 *
 * Ez a 3.0 lényege. A kiállítás egy űrlap: a sofőr kiválasztja a partnert,
 * és **három tétel** közül tölti ki, amelyik van — HSO, moslék, általános.
 * Amelyikbe nem ír semmit, arra nem készül okmány; ha HSO-ba és moslékba
 * is ír, **két külön okmány** készül, külön sorszámmal.
 *
 * Felugró ablakot nem használunk: minden megerősítés és hibaüzenet a lapon
 * belül jelenik meg. (3.0 / 0.)
 */

/**
 * A raktár utáni fülek: ott már több sofőr rakománya áll egymás mellett,
 * és a kiadás is sofőrökön átnyúlva történik. Aki csak a sajátját látja,
 * annak ezek a fülek nem jelennek meg. (2026-11-04)
 */
const IRODAI_FULEK = ['raktar', 'kiadott', 'szurt'];

/** A folyamat lépései fülenként, a leírás sorrendjében. (3.0 / 5.) */
const FULEK: Array<[string, string]> = [
  ['kiallitott', 'Kiállított'],
  ['auton', 'Autón'],
  ['raktar', 'Raktár'],
  ['kiadott', 'Kiadott'],
  ['szurt', 'Szűrt'],
  ['kimutatas', 'Kimutatás'],
];

const SZUROK: Array<[string, string]> = [
  ['mind', 'Mind'],
  ['begyujto', 'Begyűjtő'],
  ['HSO', 'HSO'],
  ['MO', 'Moslék'],
  ['ALT', 'Általános'],
  ['MHJ', 'Meghiúsult'],
  ['storno', 'Sztornó'],
];

/**
 * Amit a sofőr/nap sáv típusválasztója felkínál.
 *
 * A fenti csipekből csak a fajták, a hely szerinti szűrők (autón, raktárban)
 * nélkül — azokat a csipeken állítják. (2026-10-07)
 */
const TIPUS_SZUROK: Array<[string, string]> = [
  ['mind', 'mindegyik'],
  ['HSO', 'Fáradt olaj (HSO)'],
  ['MO', 'Moslék (MO)'],
  ['ALT', 'Általános'],
  ['MHJ', 'Meghiúsult'],
];

/** Mi lett a kiállításkor magától induló levéllel. (2026-09-29) */
interface LevelEredmeny {
  szam: string;
  sikeres: boolean;
  cimzett?: string;
  teszt_mod?: boolean;
  hiba?: string;
}

interface AlapValasz {
  tetelek: string[];
  okok: string[];
  mai: SzallitolevelSor[];
}

const URES = {
  hso: { kg: '', elvitt: '', otthagyott: '', iscc1: false },
  mo: { kg: '', elvitt: '', otthagyott: '' },
  alt: { tetel: '', mennyiseg: '', megjegyzes: '' },
};

export function Szallitolevel({ kezdo }: { kezdo?: SzlevKezdo | null }) {
  const en = useFelhasznalo();
  const alapadatok = useAlapadatok();
  /*
   * A címkére a cégnév és a telefonszám is rákerül — a cégadatokból, nem
   * a kódból. Ha egyszer számot váltunk, a címke magától követi.
   * (2026-10-07, a megrendelő kérése)
   */
  const cegAdat = {
    nev: alapadatok.beallitasok.ceg_rovid_nev || alapadatok.beallitasok.ceg_teljes_nev || '',
    /* A törzsben több szám is állhat vesszővel; a címkére az első kell. */
    telefon: (alapadatok.beallitasok.ceg_telefon ?? '').split(',')[0]?.trim() ?? '',
  };
  /* Mekkora matrica kell a tartalomhoz — ezt írjuk ki a panelen. */
  const kellMeret = cimkeMeret(cimkeTartalom('HSO/000/000000/01', cegAdat));
  const kiallithat = useMuvelet('szallito_kiall');
  /** A szállítólevelek letöltése Excelbe — területenkénti letöltési jog. */
  const szlevLetolthet = useMuvelet('export_szlev');
  const stornozhat = useMuvelet('szallito_storno');
  /** Raktárba adott tételt csak külön engedéllyel lehet sztornózni. (2026-10-06) */
  const raktarbolIsStornozhat = useMuvelet('szallito_storno_raktar');
  const kuldhet = useMuvelet('email_kuldes');

  const [ful, setFul] = useState('kiallitott');
  /** A sofőr csak a saját okmányait látja — a szerver is így adja. */
  const csakSajat = useCsakSajatSzlev();
  const [soforSzuro, setSoforSzuro] = useState('');
  const [datumSzuro, setDatumSzuro] = useState('');
  const [lista, setLista] = useState<SzlevLista | null>(null);
  const [szuro, setSzuro] = useState('mind');
  const [kereses, setKereses] = useState('');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState('');
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  /* A saját aláírás beállítása — a mentés után frissítjük a jelzést. */
  const [alairasPanel, setAlairasPanel] = useState(false);
  const [vanAlairas, setVanAlairas] = useState(!!en?.van_alairas);

  /* --- kiállítás --- */
  const [urlap, setUrlap] = useState<'nincs' | 'begyujtes' | 'meghiusulas'>('nincs');
  const [partnerKereses, setPartnerKereses] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [partner, setPartner] = useState<EtteremSor | null>(null);
  const [alap, setAlap] = useState<AlapValasz | null>(null);
  const [ertekek, setErtekek] = useState(structuredClone(URES));
  const [mhjOk, setMhjOk] = useState('');
  const [mhjMegjegyzes, setMhjMegjegyzes] = useState('');
  const [alairas, setAlairas] = useState<string | null>(null);
  const [alairasNelkul, setAlairasNelkul] = useState(false);
  const [mentes, setMentes] = useState(false);

  /* --- hordócímke (3.0 / 5.7) --- */
  const [nyomtato, setNyomtato] = useState<NyomtatoBeallitas>(NYOMTATO_ALAP);
  const [nyPanel, setNyPanel] = useState(false);
  /** A megőrzött okmányok letöltése időszakra, ZIP-ben. (3.0 / 10.) */
  const [zipPanel, setZipPanel] = useState(false);
  const [zipTol, setZipTol] = useState(() => new Date().toISOString().slice(0, 7));
  const [zipIg, setZipIg] = useState(() => new Date().toISOString().slice(0, 7));
  const [nyAllapot, setNyAllapot] = useState<{ szoveg: string; rendben: boolean | null }>({
    szoveg: '',
    rendben: null,
  });
  /** Kiállítás után felajánljuk a címkéket — annyit, ahány hordót elvitt. */
  const [cimkek, setCimkek] = useState<Array<{ id: number; szam: string; tipus: string; db: number }>>(
    [],
  );

  /** Melyik sort kérdezzük vissza sztornózás előtt. */
  const [stornoSor, setStornoSor] = useState<SzallitolevelSor | null>(null);
  /** Hány e-mail címe van a kiválasztott partnernek. null = még nem tudjuk. */
  const [emailDb, setEmailDb] = useState<number | null>(null);
  const [kuldendo, setKuldendo] = useState<LevelKeres | null>(null);
  const [stornoOk, setStornoOk] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setKeresesKesleltetve(kereses), 250);
    return () => clearTimeout(t);
  }, [kereses]);

  /** A saját nyomtatóbeállítás — felhasználónként külön. (3.0 / 5.7) */
  useEffect(() => {
    api
      .get<{ beallitas: NyomtatoBeallitas }>('/api/sajat/nyomtato')
      .then((v) => setNyomtato(v.beallitas))
      .catch(() => {
        /* ha nem jön meg, marad az alapbeállítás */
      });
  }, []);

  const betolt = useCallback(async () => {
    try {
      const p = new URLSearchParams({ szuro });
      if (keresesKesleltetve) p.set('q', keresesKesleltetve);
      if (soforSzuro) p.set('sofor', soforSzuro);
      if (datumSzuro) p.set('datum', datumSzuro);
      setLista(await api.get<SzlevLista>(`/api/szallitolevel?${p}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A szállítóleveleket nem sikerült betölteni.');
    }
  }, [szuro, keresesKesleltetve, soforSzuro, datumSzuro]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /** Partnerkeresés név, cím vagy partnerkód szerint. */
  useEffect(() => {
    if (partnerKereses.trim().length < 2) {
      setTalalatok([]);
      return;
    }
    let ervenyes = true;
    const t = setTimeout(async () => {
      try {
        const v = await api.get<{ sorok: EtteremSor[] }>(
          `/api/etterem?q=${encodeURIComponent(partnerKereses.trim())}&limit=8&csak_aktiv=1`,
        );
        if (ervenyes) setTalalatok(v.sorok);
      } catch {
        /* a keresés hibája nem akaszthatja meg az űrlapot */
      }
    }, 220);
    return () => {
      ervenyes = false;
      clearTimeout(t);
    };
  }, [partnerKereses]);

  async function partnertValaszt(e: EtteremSor) {
    setPartner(e);
    setTalalatok([]);
    setPartnerKereses('');
    try {
      setAlap(await api.get<AlapValasz>(`/api/szallitolevel/alap?etterem_id=${e.id}`));
    } catch {
      setAlap(null);
    }
  }

  /**
   * Ha a sofőri járatlapról jöttünk, az űrlap magától megnyílik a partnerrel
   * és a beírt mennyiségekkel — a sofőr ne gépelje be kétszer ugyanazt.
   */
  useEffect(() => {
    if (!kezdo) return;
    let ervenyes = true;
    void (async () => {
      try {
        const e = await api.get<EtteremSor>(`/api/etterem/${kezdo.etterem_id}`);
        if (!ervenyes) return;
        setFul('kiallitott');
        setUrlap(kezdo.mhj ? 'meghiusulas' : 'begyujtes');
        await partnertValaszt(e);
        if (!ervenyes) return;
        setErtekek((elozo) => ({
          ...elozo,
          hso: {
            ...elozo.hso,
            kg: kezdo.hso_kg ? String(kezdo.hso_kg) : elozo.hso.kg,
            elvitt: kezdo.hso_elvitt ? String(kezdo.hso_elvitt) : elozo.hso.elvitt,
          },
          mo: {
            ...elozo.mo,
            kg: kezdo.mo_kg ? String(kezdo.mo_kg) : elozo.mo.kg,
            elvitt: kezdo.mo_elvitt ? String(kezdo.mo_elvitt) : elozo.mo.elvitt,
          },
        }));
      } catch (h) {
        setHiba(h instanceof Error ? h.message : 'A partnert nem sikerült betölteni.');
      }
    })();
    return () => {
      ervenyes = false;
    };
    // A partnertValaszt a képernyő saját függvénye, nem változik menet közben.
  }, [kezdo]);

  function urlapotZar() {
    setUrlap('nincs');
    setPartner(null);
    setErtekek(structuredClone(URES));
    setMhjOk('');
    setMhjMegjegyzes('');
    setAlairas(null);
    setAlairasNelkul(false);
  }

  /**
   * Élő hordóegyenleg: nálunk nyilvántartva → új kintlévő → pótlandó.
   *
   * A partnernél **egy darabszám** van, típusra bontás nélkül: „minden hordó
   * hordó" (a megrendelő döntése, 2026-11-04). A HSO és a moslék cseréje
   * külön látszik az okmányon — a nyilvántartott darabszám viszont egy.
   *
   * Amíg a partnert nem mérték fel, nem írunk ki darabszámot: a nulla és a
   * „nincs felmérve" nem ugyanaz.
   */
  function hordoInfo() {
    if (!partner) return null;
    const kint = partner.hordo_kint ?? null;
    const elvitt = (Number(ertekek.hso.elvitt) || 0) + (Number(ertekek.mo.elvitt) || 0);
    const otthagyott = (Number(ertekek.hso.otthagyott) || 0) + (Number(ertekek.mo.otthagyott) || 0);
    const potlando = Math.max(0, elvitt - otthagyott);
    if (kint === null) {
      return (
        'A kint lévő hordó még nincs felmérve ennél a partnernél — a Partnerek képernyőn beírható.' +
        (potlando ? ` Most ${potlando} hordót pótolni kell.` : '')
      );
    }
    if (!elvitt && !otthagyott) return `Nálunk nyilvántartva: ${kint} db`;
    const uj = Math.max(0, kint - elvitt + otthagyott);
    return (
      `Nálunk nyilvántartva: ${kint} db · Új kintlévő: ${uj} db` +
      (potlando ? ` · ${potlando} hordót pótolni kell` : '')
    );
  }

  async function kiallit() {
    if (!partner || mentes) return;
    const van =
      urlap === 'meghiusulas' ||
      Number(ertekek.hso.kg) ||
      Number(ertekek.hso.elvitt) ||
      Number(ertekek.hso.otthagyott) ||
      Number(ertekek.mo.kg) ||
      Number(ertekek.mo.elvitt) ||
      Number(ertekek.mo.otthagyott) ||
      ertekek.alt.tetel;
    if (!van) {
      setHiba('Adj meg legalább egy tételt.');
      return;
    }
    // Aláírás nélkül is ki lehet állítani, de csak tudatosan: a második
    // kattintás megy át. Felugró kérdés helyett — az néma nemmel térhet vissza.
    if (!alairas && urlap !== 'meghiusulas' && !alairasNelkul) {
      setAlairasNelkul(true);
      setHiba('Nincs aláírás. Kattints még egyszer a kiállításhoz, vagy írasd alá.');
      return;
    }

    setMentes(true);
    try {
      const test =
        urlap === 'meghiusulas'
          ? { etterem_id: partner.id, mhj: { ok: mhjOk || alap?.okok[0], megjegyzes: mhjMegjegyzes }, alairas }
          : {
              etterem_id: partner.id,
              hso: {
                kg: Number(ertekek.hso.kg) || null,
                elvitt: Number(ertekek.hso.elvitt) || null,
                otthagyott: Number(ertekek.hso.otthagyott) || null,
                iscc1: ertekek.hso.iscc1,
              },
              mo: {
                kg: Number(ertekek.mo.kg) || null,
                elvitt: Number(ertekek.mo.elvitt) || null,
                otthagyott: Number(ertekek.mo.otthagyott) || null,
              },
              alt: {
                tetel: ertekek.alt.tetel || null,
                mennyiseg: Number(ertekek.alt.mennyiseg) || null,
                megjegyzes: ertekek.alt.megjegyzes || null,
              },
              alairas,
            };
      const v = await api.post<{ sorok: SzallitolevelSor[]; levelek?: LevelEredmeny[] }>(
        '/api/szallitolevel',
        test,
      );
      /*
       * A levél a kiállítással együtt megy ki. Itt mondjuk meg, mi lett
       * vele — ha nem ment ki, az a fontosabb üzenet.
       * (A megrendelő döntése, 2026-09-29.)
       */
      const levelek = v.levelek ?? [];
      const kiment = levelek.filter((l) => l.sikeres);
      const bukott = levelek.filter((l) => !l.sikeres);
      const uzenetAlap = `${v.sorok.length} okmány elkészült: ${v.sorok.map((x) => x.szam).join(', ')}.`;
      if (bukott.length > 0) {
        setUzenet(uzenetAlap);
        setHiba(
          `Az okmány elkészült, de a levél nem ment ki: ${bukott
            .map((l) => `${l.szam} — ${l.hiba ?? 'ismeretlen hiba'}`)
            .join(' · ')}. A sor végi „Küldés" gombbal újrapróbálható.`,
        );
      } else if (kiment.length > 0) {
        setUzenet(
          `${uzenetAlap} A levél elment ide: ${[...new Set(kiment.map((l) => l.cimzett))].join(', ')}.` +
            (kiment[0]?.teszt_mod ? ' (Teszt mód: a belső címre ment.)' : ''),
        );
        setHiba(null);
      } else {
        setUzenet(`${uzenetAlap} A nyomtatási kép külön ablakban nyílik.`);
        setHiba(null);
      }
      urlapotZar();
      await betolt();
      // Ha használ címkenyomtatót, felajánljuk a hordócímkéket. (3.0 / 5.7)
      setCimkek(
        nyomtato.be
          ? v.sorok
              .map((sor) => ({ id: sor.id, szam: sor.szam, tipus: sor.tipus, db: cimkeDb(sor) }))
              .filter((sor) => sor.db > 0)
          : [],
      );
      await szlevNyomtatas(v.sorok.map((x) => x.id));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A szállítólevél nem készült el.');
    } finally {
      setMentes(false);
    }
  }

  /** Hány címke tartozik egy sorhoz: annyi, ahány hordót elvitt. */
  const cimkeDb = (sor: SzallitolevelSor) =>
    (sor.tipus === 'HSO' || sor.tipus === 'MO') && sor.allapot === 'kiallitva'
      ? Math.max(0, Math.round(sor.elvitt_hordo ?? 0))
      : 0;

  async function nyomtatoMentes(uj: NyomtatoBeallitas) {
    setNyomtato(uj);
    try {
      const v = await api.post<{ beallitas: NyomtatoBeallitas }>('/api/sajat/nyomtato', uj);
      setNyomtato(v.beallitas);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A beállítást nem sikerült menteni.');
    }
  }

  /*
   * Csendes visszakapcsolás a nyomtatóhoz. (2026-11-04)
   *
   * Ha ezen a gépen már párosítottunk, a lap újratöltése után kérdés nélkül
   * visszakapcsolunk. Enélkül minden nyomtatásnál újra feljött az eszközlista.
   */
  useEffect(() => {
    if (!nyTamogatott() || parositottNev()) return;
    void nyVisszaKapcsol().then((nev) => {
      if (nev) setNyAllapot({ szoveg: `Nyomtató: ${nev}`, rendben: true });
    });
  }, [nyPanel]);

  async function parosit(mod: ParositasMod = 'szurt') {
    setNyAllapot({ szoveg: 'Keresés…', rendben: null });
    try {
      const nev = await nyParosit(mod);
      setNyAllapot({ szoveg: `Párosítva: ${nev}`, rendben: true });
      await nyomtatoMentes({ ...nyomtato, nev });
    } catch (h) {
      setNyAllapot({
        szoveg: h instanceof Error ? h.message : 'A párosítás nem sikerült.',
        rendben: false,
      });
    }
  }

  /**
   * Próbacímke a beállítások hangolásához — okmány kiállítása nélkül.
   * (a megrendelő kérése, 2026-10-01)
   */
  async function proba() {
    setNyAllapot({ szoveg: 'Próbacímke küldése…', rendben: null });
    try {
      await probaNyomtatas(nyomtato, cegAdat);
      setNyAllapot({ szoveg: 'A próbacímke elment a nyomtatóra.', rendben: true });
    } catch (h) {
      setNyAllapot({
        szoveg: h instanceof Error ? h.message : 'A próbanyomtatás nem sikerült.',
        rendben: false,
      });
    }
  }

  /** Nyomtatás, majd följegyezzük a szerveren, hogy hány címke ment ki. */
  async function cimketNyomtatni(sor: { id: number; szam: string; db: number }) {
    setNyAllapot({ szoveg: 'Nyomtatás…', rendben: null });
    try {
      const mennyi = await cimketNyomtat(sor.szam, sor.db, nyomtato, cegAdat);
      await api.post(`/api/szallitolevel/${sor.id}/cimke`, { db: mennyi });
      setNyAllapot({ szoveg: `${mennyi} címke elküldve a nyomtatóra.`, rendben: true });
    } catch (h) {
      setNyAllapot({
        szoveg: h instanceof Error ? h.message : 'A nyomtatás nem sikerült.',
        rendben: false,
      });
    }
  }

  /** Egy kiadás négy okmánya külön ablakban. */
  async function kiadasOkmanyok(id: number) {
    try {
      await kiadasOkmanyokAblak(id);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az okmányok nem nyíltak meg.');
    }
  }

  async function stornoz() {
    if (!stornoSor) return;
    try {
      await api.post(`/api/szallitolevel/${stornoSor.id}/storno`, { ok: stornoOk });
      setStornoSor(null);
      setStornoOk('');
      setUzenet('A sztornó bizonylat elkészült.');
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A sztornó nem sikerült.');
    }
  }

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Szállítólevél</span>
        {/* A folyamatot követő fülek, balról jobbra. (3.0 / 5.) */}
        <div className="vsw">
          {FULEK.filter(([k]) => !csakSajat || !IRODAI_FULEK.includes(k)).map(([k, cimke]) => (
            <button key={k} className={ful === k ? 'on' : undefined} onClick={() => setFul(k)}>
              {cimke}
            </button>
          ))}
        </div>
        {ful === 'kiallitott' && (
          <input
            className="qi"
            style={{ width: 220, fontSize: 12.5 }}
            placeholder="sorszám, partner, sofőr…"
            value={kereses}
            onChange={(e) => setKereses(e.target.value)}
          />
        )}
        <div className="stats">
          <div>
            Kiállított<strong>{ft(lista?.szuro_darabok.mind ?? 0)}</strong>
          </div>
          <div>
            Autón<strong>{ft(lista?.szuro_darabok.auto ?? 0)}</strong>
          </div>
          <div>
            Raktárban<strong>{ft(lista?.szuro_darabok.raktar ?? 0)}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {ful === 'auton' && <Auton onHiba={setHiba} onUzenet={setUzenet} />}
      {ful === 'raktar' && (
        <Raktar
          onHiba={setHiba}
          onUzenet={setUzenet}
          onKiadva={(id) => void kiadasOkmanyok(id)}
          nyomtato={nyomtato}
        />
      )}
      {ful === 'szurt' && <Szurt onHiba={setHiba} onUzenet={setUzenet} />}
      {ful === 'kimutatas' && <SzlevKimutatas onHiba={setHiba} />}
      {ful === 'kiadott' && (
        <Kiadottak
          onHiba={setHiba}
          onOkmanyok={(id) => void kiadasOkmanyok(id)}
          onKuldes={
            kuldhet
              ? (k) =>
                  setKuldendo({
                    tipus: 'kiadas',
                    cim: k.szam,
                    atvevo_id: k.atvevo_id,
                    tabla: 'kiadas',
                    rekord_id: k.id,
                    adatok: {
                      bizonylatszam: k.szam,
                      kelt: k.kelt,
                      atvevo: k.atvevo_nev ?? '',
                    },
                  })
              : undefined
          }
        />
      )}

      {/*
        A küldőpanel **minden fülön** kell, nem csak a Kiállítotton: a
        Kiadott fülről is innen megy a levél az átvevőnek.

        Korábban a fül-őrségen belül állt, ezért a Kiadott fülön a Küldés
        gomb beállította ugyan a levelet, de a panel meg sem jelent — a
        kattintás után nem történt semmi, és a levélnaplóba sem került
        sor. Ezért nem ment ki egyetlen kiadási okmány sem.
        (A megrendelő jelzése, 2026-10-20.)
      */}
      {kuldendo && (
        <LevelKuldoPanel
          keres={kuldendo}
          onKesz={(u) => {
            setKuldendo(null);
            setUzenet(u);
          }}
          onMegse={() => setKuldendo(null)}
        />
      )}

      {ful !== 'kiallitott' ? null : (
      <>
      <div className="fchips">
        {SZUROK.map(([k, cimke]) => (
          <button key={k} className={`fchip${szuro === k ? ' on' : ''}`} onClick={() => setSzuro(k)}>
            {cimke}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {/*
        * Sofőr és nap szerinti szűrés. Enélkül „mi volt Marcinál kedden"
        * csak a teljes lista végiggörgetésével volt kideríthető.
        * (2026-10-05, a megrendelő kérése)
        */}
      <div className="szlszuro">
        {/* A sofőrválasztónak csak ott van értelme, ahol több sofőr látszik. */}
        {!csakSajat && (
          <label>
            Sofőr
            <select value={soforSzuro} onChange={(e) => setSoforSzuro(e.target.value)}>
              <option value="">mindegyik</option>
              {(lista?.soforok ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
        {csakSajat && <span className="hint">Itt a saját szállítóleveleid látszanak.</span>}
        <label>
          Nap
          <input type="date" value={datumSzuro} onChange={(e) => setDatumSzuro(e.target.value)} />
        </label>
        {/*
          * Típus is ide, a sofőr és a nap mellé: így egy helyen állítható
          * össze az „egy sofőr, egy nap, egy fajta" kérdés. Ugyanazt az
          * állapotot állítja, mint a fenti csipek, tehát a kettő együtt
          * mozog. (2026-10-07, a megrendelő kérése)
          */}
        <label>
          Típus
          <select value={szuro} onChange={(e) => setSzuro(e.target.value)}>
            {TIPUS_SZUROK.map(([k, cimke]) => (
              <option key={k} value={k}>
                {cimke}
              </option>
            ))}
          </select>
        </label>
        {/*
          * A lista letöltése Excelbe — **pontosan azzal a szűréssel**, ami
          * a képernyőn áll. (A megrendelő kérése, 2026-10-21.)
          */}
        {szlevLetolthet && (
          <button
            className="btn sm"
            title="A most látható szállítólevelek letöltése Excelbe"
            onClick={() => {
              const p = new URLSearchParams({ szuro });
              if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
              if (soforSzuro) p.set('sofor', soforSzuro);
              if (datumSzuro) p.set('datum', datumSzuro);
              letolt(`/api/export/szallitolevelek.xlsx?${p}`);
            }}
          >
            Excel
          </button>
        )}
        {(soforSzuro || datumSzuro || szuro !== 'mind') && (
          <>
            <button
              className="btn sm"
              onClick={() => {
                setSoforSzuro('');
                setDatumSzuro('');
                setSzuro('mind');
              }}
            >
              Szűrés törlése
            </button>
            <span className="hint">
              {ft(lista?.talalat ?? 0)} okmány felel meg
              {(lista?.talalat ?? 0) > (lista?.sorok.length ?? 0)
                ? ` — ebből ${ft(lista?.sorok.length ?? 0)} látszik`
                : ''}
            </span>
          </>
        )}
      </div>
      </>
      )}

      {ful === 'kiallitott' && (
        <>
      {kiallithat && (
        <div className="actbar">
          <button className="btn pri sm" onClick={() => setUrlap('begyujtes')}>
            + Új szállítólevél
          </button>
          <button className="btn sm" onClick={() => setUrlap('meghiusulas')}>
            + Meghiúsulás
          </button>
          <button className="btn sm" onClick={() => setZipPanel(!zipPanel)}>
            PDF-ek letöltése
          </button>
          <button className="btn sm" onClick={() => setNyPanel(!nyPanel)}>
            Címkenyomtató
          </button>
          <button className="btn sm" onClick={() => setAlairasPanel(!alairasPanel)}>
            Saját aláírás
          </button>
          <span className="hint">
            {vanAlairas
              ? 'A saját aláírásod minden okmányra rákerül.'
              : 'Még nincs saját aláírásod — a „Saját aláírás” gombbal rajzolhatod meg.'}
          </span>
        </div>
      )}

      {alairasPanel && (
        <SajatAlairas
          vanMar={vanAlairas}
          onKesz={(u) => {
            setUzenet(u);
            setVanAlairas(!u.includes('törölve'));
            setAlairasPanel(false);
          }}
          onMegse={() => setAlairasPanel(false)}
        />
      )}

      {/* A megőrzött okmányok egy időszakra, ZIP-ben. (3.0 / 10.) */}
      {zipPanel && (
        <div className="npform" style={{ maxWidth: 560, marginTop: 12 }}>
          <h4>Megőrzött okmányok letöltése</h4>
          <div className="c" style={{ marginBottom: 10 }}>
            A kiállított okmányok PDF-jei a szerveren maradnak. Itt egy időszak anyaga
            letölthető egyetlen csomagban — például könyvelésnek vagy ellenőrzéshez.
          </div>
          <div className="tetelmezok">
            <label>
              Ettől a hónaptól
              <input type="month" value={zipTol} onChange={(e) => setZipTol(e.target.value)} />
            </label>
            <label>
              Eddig a hónapig
              <input type="month" value={zipIg} onChange={(e) => setZipIg(e.target.value)} />
            </label>
          </div>
          <div className="btns">
            <button
              className="btn pri sm"
              disabled={!zipTol || !zipIg || zipTol > zipIg}
              onClick={() =>
                window.open(
                  `/api/szallitolevel/pdf-csomag?tol=${zipTol}&ig=${zipIg}`,
                  '_blank',
                  'noopener',
                )
              }
            >
              Letöltés
            </button>
            <button className="btn sm" onClick={() => setZipPanel(false)}>
              Bezárás
            </button>
          </div>
        </div>
      )}

      {/* Hordócímke nyomtató — felhasználónként. (3.0 / 5.7) */}
      {nyPanel && (
        <div className="npform" style={{ maxWidth: 560, marginTop: 12 }}>
          <h4>Hordócímke nyomtató</h4>
          <div className="c" style={{ marginBottom: 10 }}>
            Ha bekapcsolod, a szállítólevél kiállítása után a program felajánlja a címkék
            nyomtatását — annyit, ahány hordót elvittél. A címkén csak a sorszám van, nagy betűvel.
          </div>
          <label className="kapcs">
            <input
              type="checkbox"
              checked={nyomtato.be}
              onChange={(e) => void nyomtatoMentes({ ...nyomtato, be: e.target.checked })}
            />
            Használom a címkenyomtatót
          </label>
          <div className="tetelmezok" style={{ marginTop: 8 }}>
            <label>
              Papírszélesség
              <select
                value={nyomtato.szeles}
                onChange={(e) =>
                  void nyomtatoMentes({
                    ...nyomtato,
                    szeles: Number(e.target.value) as NyomtatoBeallitas['szeles'],
                  })
                }
              >
                {[56, 58, 80].map((sz) => (
                  <option key={sz} value={sz}>
                    {sz} mm
                  </option>
                ))}
              </select>
            </label>
            <label>
              Példányszám hordónként
              <input
                inputMode="numeric"
                onFocus={(e) => e.currentTarget.select()}
                value={nyomtato.pld}
                onChange={(e) => setNyomtato({ ...nyomtato, pld: Number(e.target.value) || 1 })}
                onBlur={() => void nyomtatoMentes(nyomtato)}
              />
            </label>
            {/*
              * A nyomtató nyelve. (2026-10-07)
              *
              * A blokknyomtató ESC/POS-t ért, a matricás címkenyomtató nem:
              * az a saját nyelvén várja a címkét, a matrica méretével és
              * külön nyomtatási paranccsal. Ugyanaz a bájtsor az egyiken
              * kijön, a másikon csak felpörgeti a motort.
              */}
            <label>
              A nyomtató fajtája
              <select
                value={nyomtato.nyelv}
                onChange={(e) =>
                  void nyomtatoMentes({
                    ...nyomtato,
                    nyelv: e.target.value as NyomtatoBeallitas['nyelv'],
                  })
                }
              >
                {NYOMTATO_NYELVEK.map((ny) => (
                  <option key={ny} value={ny}>
                    {NYOMTATO_NYELV_NEVEK[ny]}
                  </option>
                ))}
              </select>
            </label>
            {nyomtato.nyelv !== 'escpos' && (
              <>
                <label>
                  Matrica szélessége (mm)
                  <input
                    inputMode="numeric"
                    onFocus={(e) => e.currentTarget.select()}
                    value={nyomtato.cimke_szeles}
                    onChange={(e) =>
                      setNyomtato({ ...nyomtato, cimke_szeles: Number(e.target.value) || 50 })
                    }
                    onBlur={() => void nyomtatoMentes(nyomtato)}
                  />
                </label>
                <label>
                  Matrica magassága (mm)
                  <input
                    inputMode="numeric"
                    onFocus={(e) => e.currentTarget.select()}
                    value={nyomtato.cimke_magas}
                    onChange={(e) =>
                      setNyomtato({ ...nyomtato, cimke_magas: Number(e.target.value) || 30 })
                    }
                    onBlur={() => void nyomtatoMentes(nyomtato)}
                  />
                </label>
              </>
            )}
            {/*
              * Sorköz és papírtolás: készülékenként más, ezért állítható.
              * (a megrendelő kérése, 2026-10-01)
              */}
            <label>
              Sorköz (0 = gyári)
              <input
                inputMode="numeric"
                onFocus={(e) => e.currentTarget.select()}
                value={nyomtato.sorkoz}
                onChange={(e) => setNyomtato({ ...nyomtato, sorkoz: Number(e.target.value) || 0 })}
                onBlur={() => void nyomtatoMentes(nyomtato)}
              />
            </label>
            <label>
              Papírtolás a címke után (sor)
              <input
                inputMode="numeric"
                onFocus={(e) => e.currentTarget.select()}
                value={nyomtato.tolas}
                onChange={(e) => setNyomtato({ ...nyomtato, tolas: Number(e.target.value) || 0 })}
                onBlur={() => void nyomtatoMentes(nyomtato)}
              />
            </label>
          </div>
          <div className="hint">
            {nyomtato.nyelv === 'escpos' ? (
              <>
                A sorköz pontban értendő: minél nagyobb, annál ritkábban fog a nyomtató. Nullán a
                készülék gyári sorköze marad. A papírtolás adja a letéphető részt — ezzel lehet
                eltalálni a címkék közti rést.
              </>
            ) : (
              <>
                Matricás nyomtatónál a sorköz és a papírtolás nem számít. A címkére csak a
                sorszám kerül: az <b>MO / HSO</b> jelölés akkora betűt kap, amekkora még elfér a
                beállított matricán, a sorszám többi része közepes méretben megy.{' '}
                <b>Nagyobb matricán nagyobb a jelölés</b> — ezért érdemes a beállított méretet a
                tényleges matricához igazítani. Ehhez a címkéhez legalább{' '}
                <b>
                  {kellMeret.szeles} × {kellMeret.magas} mm
                </b>{' '}
                kell; a beállított méret most {nyomtato.cimke_szeles} × {nyomtato.cimke_magas} mm.
                Ha a próbanyomtatás néma marad, váltsd át a másik címkenyelvre — a két elterjedt
                nyelv közül az egyiket minden ilyen nyomtató érti.
              </>
            )}{' '}
            A „Próbanyomtatás" gombbal kipróbálható, okmány kiállítása nélkül.
          </div>

          <div className="nyallap">
            {nyAllapot.szoveg ? (
              <span className={nyAllapot.rendben === false ? 'nynem' : nyAllapot.rendben ? 'nyok' : 'nyvar'}>
                {nyAllapot.szoveg}
              </span>
            ) : parositottNev() ? (
              <span className="nyok">Párosítva: {parositottNev()}</span>
            ) : nyTamogatott() ? (
              <span className="nyvar">
                Még nincs párosítva
                {nyomtato.nev || nyEmlek()?.nev ? ` — legutóbb: ${nyomtato.nev || nyEmlek()?.nev}` : ''}.
                A listában csak a nyomtatónak látszó eszközök jelennek meg. Ha a nyomtató nincs
                köztük, a „Minden eszköz" gomb mindent felsorol.
              </span>
            ) : (
              <span className="nynem">
                Ez a böngésző nem tud Bluetooth-nyomtatót kezelni. Androidon Chrome kell hozzá, és
                biztonságos (https) cím. A címke előnézete így is működik.
              </span>
            )}

            {/*
              * A csatorna külön soron, az állapottól függetlenül: párosítás
              * után a zöld üzenet elnyomta, pedig épp akkor kell. Ha egy
              * nyomtató makacskodik, ebből derül ki, melyik csatornát
              * választotta a program. (2026-10-07)
              */}
            {parositottCsatorna() && (
              <span className="nycsatorna">csatorna: {parositottCsatorna()}</span>
            )}
          </div>

          <div className="btns">
            {/*
              * Három út, egyre szélesebb listával. (2026-11-04)
              *
              * A megrendelő jelezte, hogy túl sok eszközt kínál fel a program.
              * Ha már párosítottunk ezen a gépen, elsőként **csak azt az egy
              * nyomtatót** kérjük el név szerint; másodszor a nyomtatónak látszó
              * eszközöket; és csak a végén mindent.
              */}
            {nyTamogatott() && nyEmlek()?.nev && (
              <button
                className="btn sm"
                onClick={() => void parosit('emlekezett')}
                title="A legutóbbi nyomtató, név szerint"
              >
                Kapcsolódás: {nyEmlek()!.nev}
              </button>
            )}
            {nyTamogatott() && (
              <button
                className="btn sm"
                onClick={() => void parosit('szurt')}
                title="A nyomtatónak látszó közeli eszközök"
              >
                {nyEmlek()?.nev ? 'Másik nyomtató' : 'Nyomtató keresése'}
              </button>
            )}
            {nyTamogatott() && (
              <button
                className="btn sm"
                onClick={() => void parosit('mind')}
                title="Minden közeli Bluetooth-eszköz — ha a nyomtató a szűrt listából kimaradt"
              >
                Minden eszköz
              </button>
            )}
            {nyTamogatott() && (nyEmlek()?.nev || parositottNev()) && (
              <button
                className="btn sm"
                onClick={() => {
                  nyLevalaszt();
                  setNyAllapot({ szoveg: 'A nyomtatót elfelejtettük.', rendben: null });
                }}
                title="A megjegyzett nyomtató törlése erről a gépről"
              >
                Elfelejtés
              </button>
            )}
            {nyTamogatott() && (
              <button className="btn sm" onClick={() => void proba()}>
                Próbanyomtatás
              </button>
            )}
            <button className="btn sm" onClick={() => setNyPanel(false)}>
              Bezárás
            </button>
          </div>
        </div>
      )}

      {/* Kiállítás után: mehetnek a címkék. */}
      {cimkek.length > 0 && (
        <div className="npform" style={{ maxWidth: 560, marginTop: 12 }}>
          <h4>Hordócímke</h4>
          <div className="c">
            {cimkek.reduce((a, c) => a + c.db, 0)} hordóhoz tartozik címke. Ragaszd fel, hogy később
            azonosítható legyen.
          </div>
          <div className="btns" style={{ marginTop: 12 }}>
            {cimkek.map((c) => (
              <button key={c.id} className="btn pri sm" onClick={() => void cimketNyomtatni(c)}>
                {c.tipus} — {c.db} db nyomtatása
              </button>
            ))}
            {cimkek.map((c) => (
              <button
                key={`e${c.id}`}
                className="btn sm"
                onClick={() => cimkeElonezet(c.szam, c.db, nyomtato.szeles, cegAdat)}
              >
                {c.tipus} előnézet
              </button>
            ))}
            <button className="btn sm" onClick={() => setCimkek([])}>
              Kihagyom
            </button>
          </div>
          <div className="nyallap">
            {nyAllapot.szoveg && (
              <span className={nyAllapot.rendben === false ? 'nynem' : nyAllapot.rendben ? 'nyok' : 'nyvar'}>
                {nyAllapot.szoveg}
              </span>
            )}
          </div>
        </div>
      )}

      {urlap !== 'nincs' && (
        <div className="npform szlform">
          <h4>{urlap === 'meghiusulas' ? 'Meghiúsulási jegyzőkönyv' : 'Új szállítólevél'}</h4>

          {!partner ? (
            <>
              <label className="lbl">Partner — név, cím vagy partnerkód</label>
              <input
                autoFocus
                value={partnerKereses}
                placeholder="kezdd el írni a nevet…"
                onChange={(e) => setPartnerKereses(e.target.value)}
              />
              <div className="talalatok">
                {talalatok.map((t) => (
                  <button key={t.id} className="talalat" onClick={() => void partnertValaszt(t)}>
                    <b>{t.nev}</b>
                    <small>
                      {t.kod ? `${t.kod} · ` : ''}
                      {[t.irsz, t.cim].filter(Boolean).join(' ')}
                    </small>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="partnerdoboz">
                <div>
                  <b>{partner.nev}</b>
                  <small>{[partner.irsz, partner.cim, partner.helyseg].filter(Boolean).join(' ')}</small>
                </div>
                <div className="pmezok">
                  <span className={partner.cegnev ? '' : 'hianyzik'}>
                    Cégnév: {partner.cegnev ?? 'hiányzik'}
                  </span>
                  <span className={partner.adoszam ? '' : 'hianyzik'}>
                    Adószám: {partner.adoszam ?? 'hiányzik'}
                  </span>
                  <span className={partner.ktj_azonosito ? '' : 'hianyzik'}>
                    KTJ: {partner.ktj_azonosito ?? 'hiányzik'}
                  </span>
                  <span className={partner.kod ? '' : 'hianyzik'}>
                    Partnerkód: {partner.kod ?? 'nincs kiosztva'}
                  </span>
                </div>
                <button className="btn sm" onClick={() => setPartner(null)}>
                  Másik partner
                </button>
              </div>

              {/* A partner elérhetősége itt is rögzíthető. (3.0 / 1.) */}
              <ElerhetosegDoboz
                etterem_id={partner.id}
                onEmailek={setEmailDb}
                csakUj
                cel="szallito"
              />

              {(alap?.mai.length ?? 0) > 0 && (
                <div className="uzenet flag" style={{ marginTop: 8 }}>
                  Ma már készült ide okmány: {alap!.mai.map((m) => m.szam).join(', ')}
                </div>
              )}

              {urlap === 'meghiusulas' ? (
                <>
                  <label className="lbl" style={{ marginTop: 10 }}>
                    A meghiúsulás oka
                  </label>
                  {(alap?.okok ?? []).map((o) => (
                    <label className="kapcs" key={o}>
                      <input
                        type="radio"
                        name="mhjok"
                        checked={(mhjOk || alap?.okok[0]) === o}
                        onChange={() => setMhjOk(o)}
                      />
                      {o}
                    </label>
                  ))}
                  <label className="lbl" style={{ marginTop: 10 }}>
                    Megjegyzés
                  </label>
                  <input value={mhjMegjegyzes} onChange={(e) => setMhjMegjegyzes(e.target.value)} />
                </>
              ) : (
                <>
                  <div className="tetelblokk">
                    <p className="lbl">HSO — használt sütőolaj</p>
                    <div className="tetelmezok">
                      <label>
                        Tömeg (kg)
                        <input
                          inputMode="decimal"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.hso.kg}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, hso: { ...ertekek.hso, kg: e.target.value } })
                          }
                        />
                      </label>
                      <label>
                        Elvitt hordó
                        <input
                          inputMode="numeric"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.hso.elvitt}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, hso: { ...ertekek.hso, elvitt: e.target.value } })
                          }
                        />
                      </label>
                      <label>
                        Otthagyott üres
                        <input
                          inputMode="numeric"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.hso.otthagyott}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, hso: { ...ertekek.hso, otthagyott: e.target.value } })
                          }
                        />
                      </label>
                    </div>
                    <label className="kapcs">
                      <input
                        type="checkbox"
                        checked={ertekek.hso.iscc1}
                        onChange={(e) =>
                          setErtekek({ ...ertekek, hso: { ...ertekek.hso, iscc1: e.target.checked } })
                        }
                      />
                      ISCC: havi öt tonna vagy afölött
                    </label>
                    <div className="hint">{hordoInfo()}</div>
                  </div>

                  <div className="tetelblokk">
                    <p className="lbl">Moslék</p>
                    <div className="tetelmezok">
                      <label>
                        Tömeg (kg)
                        <input
                          inputMode="decimal"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.mo.kg}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, mo: { ...ertekek.mo, kg: e.target.value } })
                          }
                        />
                      </label>
                      <label>
                        Elvitt hordó
                        <input
                          inputMode="numeric"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.mo.elvitt}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, mo: { ...ertekek.mo, elvitt: e.target.value } })
                          }
                        />
                      </label>
                      <label>
                        Otthagyott üres
                        <input
                          inputMode="numeric"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.mo.otthagyott}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, mo: { ...ertekek.mo, otthagyott: e.target.value } })
                          }
                        />
                      </label>
                    </div>
                    <div className="hint">{hordoInfo()}</div>
                  </div>

                  <div className="tetelblokk">
                    <p className="lbl">Általános</p>
                    <div className="tetelmezok">
                      <label>
                        Tétel
                        <select
                          value={ertekek.alt.tetel}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, alt: { ...ertekek.alt, tetel: e.target.value } })
                          }
                        >
                          <option value="">— nincs —</option>
                          {(alap?.tetelek ?? []).map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Mennyiség (db)
                        <input
                          inputMode="numeric"
                          onFocus={(e) => e.currentTarget.select()}
                          value={ertekek.alt.mennyiseg}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, alt: { ...ertekek.alt, mennyiseg: e.target.value } })
                          }
                        />
                      </label>
                      <label>
                        Megjegyzés
                        <input
                          value={ertekek.alt.megjegyzes}
                          onChange={(e) =>
                            setErtekek({ ...ertekek, alt: { ...ertekek.alt, megjegyzes: e.target.value } })
                          }
                        />
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/*
                * Az aláírás külön lapon nyílik, nem itt az űrlapban: a
                * rajzfelület elnyelte a görgetést, és a sofőrök folyton
                * belerajzoltak. (A sofőrök visszajelzése, 2026-10-04.)
                */}
              <AlairasGomb cimke="Átadó aláírása" ertek={alairas} onValtozott={setAlairas} />

              {/*
                * A levél a kiállítással együtt megy ki — ha nincs cím, szólunk
                * előre, hogy a helyszínen még be lehessen írni.
                * (A megrendelő döntése, 2026-09-29.)
                */}
              {emailDb === 0 && (
                <div className="uzenet flag" style={{ marginTop: 8 }}>
                  Ehhez a partnerhez <b>nincs e-mail cím</b>. Az okmány kiállítható, de nem megy ki
                  levél — ha a vendéglős tud címet adni, írd be fent, és magától kimegy.
                </div>
              )}

              {/*
                * Itt figyelmeztetünk, és nem csak a lap tetején: az egyik
                * sofőr öt napon át 35 okmányt állított ki aláírás nélkül,
                * mert a fenti halvány sort nem vette észre. Nem akadály —
                * az okmány enélkül is kiállítható. (2026-10-06)
                */}
              {!vanAlairas && (
                <div className="uzenet flag" style={{ marginTop: 8 }}>
                  <b>Nincs beállítva a saját aláírásod</b>, így erre az okmányra sem kerül rá.
                  Fent a <b>Saját aláírás</b> gombbal egyszer megrajzolod, és onnantól minden
                  okmányodra magától rákerül. Az okmány enélkül is kiállítható.
                </div>
              )}

              <div className="btns zarosav">
                <button className="btn gold" disabled={mentes} onClick={() => void kiallit()}>
                  {mentes ? 'Kiállítás…' : alairasNelkul ? 'Kiállítás aláírás nélkül' : 'Kiállítás'}
                </button>
                <button className="btn sm" onClick={urlapotZar}>
                  Mégse
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto', marginTop: 12 }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Sorszám</th>
              <th>Típus</th>
              <th>Partner</th>
              <th className="n">Kelt</th>
              <th className="n">Tömeg</th>
              <th className="n">Elvitt</th>
              <th className="n">Kintlévő</th>
              <th>Hol van</th>
              <th>Sofőr</th>
              <th>Levél</th>
              <th>Állapot</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(lista?.sorok ?? []).map((s) => (
              <tr key={s.id} className={s.allapot === 'kiallitva' ? undefined : 'nul'}>
                <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                  {s.szam}
                </td>
                <td>{s.tipus}</td>
                <td className="pn">
                  {s.etterem_nev}
                  {s.cegnev && <small>{s.cegnev}</small>}
                </td>
                <td className="calc2">{s.kelt}</td>
                <td className="calc2">{s.tomeg_kg ? `${szam(s.tomeg_kg, 0)} kg` : ''}</td>
                <td className="calc2">{s.elvitt_hordo ?? ''}</td>
                <td className="calc2">{s.kintlevo_hordo ?? ''}</td>
                <td>{s.hely_nev}</td>
                <td>{s.sofor}</td>
                {/*
                  * Kiment-e a levél. Ami kiállítva van, de nem ment róla
                  * levél, azt sárga jelöli — jellemzően azért, mert a
                  * partnernek nincs e-mail címe. A sor végi „Küldés"
                  * gombbal pótolható. (2026-10-01)
                  */}
                <td>
                  {s.email_kiment ? (
                    <span className="levelok">kiment</span>
                  ) : s.allapot === 'kiallitva' ? (
                    <span className="levelnem">nem ment</span>
                  ) : (
                    ''
                  )}
                </td>
                <td>{s.allapot_nev}</td>
                <td className="act" style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn sm" onClick={() => void szlevNyomtatas([s.id])}>
                    Nyomtat
                  </button>
                  <button className="btn sm" title="A megőrzött PDF" onClick={() => szlevPdf(s.id)}>
                    PDF
                  </button>
                  {kuldhet && s.allapot === 'kiallitva' && (
                    <button
                      className="btn sm"
                      title="A megőrzött PDF elküldése a partnernek"
                      onClick={() =>
                        setKuldendo({
                          tipus: 'szallitolevel',
                          cim: s.szam,
                          etterem_id: s.etterem_id,
                          tabla: 'szallitolevel',
                          rekord_id: s.id,
                          adatok: {
                            bizonylatszam: s.szam,
                            kelt: s.kelt,
                            partner: s.etterem_nev,
                            /* A partner cége — a saját cégünk a `{cegnev}`, azt
                               a szerver teszi az aláírásba. (2026-10-16.) */
                            partner_cegnev: s.cegnev ?? '',
                          },
                        })
                      }
                    >
                      Küldés
                    </button>
                  )}
                  {cimkeDb(s) > 0 && (
                    <button
                      className="btn sm"
                      title={`${cimkeDb(s)} hordócímke`}
                      onClick={() =>
                        nyomtato.be
                          ? void cimketNyomtatni({ id: s.id, szam: s.szam, db: cimkeDb(s) })
                          : cimkeElonezet(s.szam, cimkeDb(s), nyomtato.szeles, cegAdat)
                      }
                    >
                      Címke
                    </button>
                  )}
                  {/*
                    * Amíg az áru az autón van, a sztornó szabad. A raktárba
                    * adott tételhez külön engedély kell — ott a sztornó a
                    * raktárkészletet is átírja. Ami már ki van adva az
                    * átvevőnek, azt sehogy. (2026-10-06)
                    */}
                  {stornozhat &&
                    s.allapot === 'kiallitva' &&
                    (s.hely === 'auto' || (s.hely === 'raktar' && raktarbolIsStornozhat)) && (
                      <button
                        className="btn sm torlo"
                        title={
                          s.hely === 'raktar'
                            ? 'A tétel már a raktárban van — a sztornó a raktárkészletből is kiveszi.'
                            : undefined
                        }
                        onClick={() => setStornoSor(s)}
                      >
                        {s.hely === 'raktar' ? 'Sztornó (raktárból)' : 'Sztornó'}
                      </button>
                    )}
                </td>
              </tr>
            ))}
            {(lista?.sorok.length ?? 0) === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Még nincs kiállított szállítólevél.
                </td>
              </tr>
            )}
          </tbody>
          {/*
            * Összegsor: a szűrésnek megfelelő összes okmány, nem csak a
            * látszó sorok. A sztornózott és sztornó bizonylatok kimaradnak —
            * ugyanaz a szabály, mint az ellenőrzésnél, hogy a két szám
            * összevethető legyen. (2026-10-07, a megrendelő kérése)
            */}
          {(lista?.osszeg.db ?? 0) > 0 && (
            <tfoot>
              {/*
                * A tömegoszlopban a **látszó** halmaz teljes súlya áll, nem
                * csak a HSO-é: moslékra szűrve eddig 0 kg jött, pedig a
                * moslékhoz is írnak súlyt. A bontás mellette.
                * (2026-10-13, a megrendelő jelzése)
                */}
              <tr>
                <td className="dcol" colSpan={4}>
                  Összesen, sztornó nélkül — {ft(lista!.osszeg.db)} okmány
                </td>
                <td title="A sofőr mérése szerint, ahogy az oszlopban is áll">
                  {ft(lista!.osszeg.hso_kg + lista!.osszeg.mo_kg)} kg
                </td>
                <td title="Elvitt hordó: fáradt olaj + moslék">
                  {ft(lista!.osszeg.hso_hordo + lista!.osszeg.mo_hordo)}
                </td>
                <td className="dcol" colSpan={6}>
                  HSO {ft(lista!.osszeg.hso_kg)} kg / {ft(lista!.osszeg.hso_hordo)} hordó · moslék{' '}
                  {ft(lista!.osszeg.mo_kg)} kg / {ft(lista!.osszeg.mo_hordo)} hordó
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {stornoSor && (
        <div className="npform" style={{ marginTop: 12 }}>
          <h4>Sztornó — {stornoSor.szam}</h4>
          <div className="c" style={{ marginBottom: 8 }}>
            A sztornó önálló bizonylat, saját sorszámmal, az eredetire hivatkozva. Az eredeti okmány
            megmarad, csak jelölést kap. <b>Az indoklás kötelező.</b>
          </div>
          <input
            autoFocus
            value={stornoOk}
            placeholder="miért sztornózod?"
            onChange={(e) => setStornoOk(e.target.value)}
          />
          <div className="btns">
            <button className="btn sm torlo" disabled={!stornoOk.trim()} onClick={() => void stornoz()}>
              Sztornózás
            </button>
            <button
              className="btn sm"
              onClick={() => {
                setStornoSor(null);
                setStornoOk('');
              }}
            >
              Mégse
            </button>
          </div>
        </div>
      )}
        </>
      )}
    </>
  );
}
